import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

function Nav() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#050505]/60 border-b border-white/5"
      data-testid="monolith-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 h-16 flex items-center justify-between">
        <Link to="/designs" className="font-mono text-[11px] tracking-[0.3em] uppercase">
          Credit·Stone / Monolith
        </Link>
        <div className="hidden md:flex items-center gap-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
          <a href="#index">Index</a>
          <a href="#metrics">Metrics</a>
          <a href="#contact">Contact</a>
        </div>
        <Link
          to="/designs"
          className="font-mono text-[10px] tracking-[0.3em] uppercase px-4 py-2 rounded-full border border-white/15 hover:bg-white/5"
        >
          ← All Variants
        </Link>
      </div>
    </header>
  );
}

const services = [
  { n: "I", t: "Origination", d: "Pre-cleared credit, deployable in seconds." },
  { n: "II", t: "Custody", d: "Bankruptcy-remote storage across two trustees." },
  { n: "III", t: "Clearing", d: "T+0 atomic settlement on a single ledger." },
  { n: "IV", t: "Reserve", d: "Tiered yield strata for treasury surplus." },
];

const stats = [
  { v: "4.2", u: "B$", l: "Cleared on platform" },
  { v: "212", u: "BPS", l: "Average yield" },
  { v: "00", u: "S", l: "Settlement latency" },
  { v: "37", u: "JURISDICTIONS", l: "Operational reach" },
];

export default function Monolith() {
  return (
    <main className="min-h-screen bg-[#040404] text-[#F5F1EA] grain" data-testid="monolith-page">
      <Nav />

      {/* HERO — split slab */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0 px-6 sm:px-10 lg:px-0">
          {/* LEFT — text slab */}
          <div className="lg:col-span-7 lg:px-14 lg:py-16 relative">
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] uppercase text-[#8C8C8C] mb-10">
              <span className="w-8 h-[1px] bg-[#404040]" />
              <span>Folio 01 — Anno MMXXVI</span>
            </div>

            <motion.h1
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.4, ease }}
              className="font-display uppercase leading-[0.82] tracking-[-0.02em] text-[14vw] lg:text-[10vw]"
            >
              Carved
              <br />
              <span className="italic text-[#F5F1EA]/70">in</span> capital.
            </motion.h1>

            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "120px" }}
              transition={{ duration: 1.4, ease, delay: 0.4 }}
              className="h-px bg-[#404040] mt-12 mb-8"
            />

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1, ease, delay: 0.5 }}
              className="font-body text-sm sm:text-base text-[#8C8C8C] max-w-md leading-relaxed"
            >
              Credit Stone is the institutional backbone for treasuries that
              prefer their finance the way they prefer their architecture —
              quiet, load-bearing, and made to outlast the cycle.
            </motion.p>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1, ease, delay: 0.7 }}
              className="mt-10 flex items-center gap-3"
            >
              <button className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 rounded-full bg-[#F5F1EA] text-[#040404] hover:bg-white">
                Request Access
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <button className="font-mono text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5">
                Read the Folio
              </button>
            </motion.div>
          </div>

          {/* RIGHT — vertical rock plate */}
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2, ease, delay: 0.3 }}
            className="lg:col-span-5 relative h-[60vh] lg:h-[88vh] mt-12 lg:mt-0 lg:border-l border-[#1A1A1A]"
          >
            <img
              src="https://images.pexels.com/photos/12184310/pexels-photo-12184310.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=720"
              alt="Monolith"
              className="w-full h-full object-cover grayscale contrast-110"
              draggable="false"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(90deg, rgba(4,4,4,1) 0%, rgba(4,4,4,0) 25%, rgba(4,4,4,0) 75%, rgba(4,4,4,0.8) 100%)",
              }}
            />
            <div className="absolute top-6 right-6 font-mono text-[10px] tracking-[0.35em] uppercase text-[#F5F1EA]/70">
              Plate · 01
            </div>
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between font-mono text-[10px] tracking-[0.35em] uppercase text-[#F5F1EA]/70">
              <span>Igneous · Black Granite</span>
              <span>47.3769° N</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INDEX STRIP */}
      <section id="index" className="border-y border-[#1A1A1A] py-20 sm:py-28">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#8C8C8C] mb-4">
              Index
            </div>
            <h2 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]">
              The four chambers.
            </h2>
          </div>
          <ul className="lg:col-span-9 divide-y divide-[#1A1A1A]">
            {services.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.9, ease, delay: i * 0.08 }}
                className="grid grid-cols-12 items-baseline py-7 gap-6 group hover:bg-white/[0.02] transition-colors px-2 -mx-2 rounded"
              >
                <span className="col-span-2 font-mono text-xs tracking-[0.3em] text-[#8C8C8C]">
                  {s.n}
                </span>
                <span className="col-span-5 font-display uppercase text-2xl sm:text-3xl">
                  {s.t}
                </span>
                <span className="col-span-4 font-body text-sm text-[#8C8C8C] leading-relaxed">
                  {s.d}
                </span>
                <ArrowUpRight className="col-span-1 w-5 h-5 justify-self-end text-[#8C8C8C] group-hover:text-[#F5F1EA] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* METRICS */}
      <section id="metrics" className="py-24 sm:py-32">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#8C8C8C] mb-10">
            Ledger of weight
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-[#1A1A1A] border border-[#1A1A1A]">
            {stats.map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.9, ease, delay: i * 0.08 }}
                className="p-8 lg:p-10"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl sm:text-6xl tracking-tight">
                    {s.v}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-[#8C8C8C]">
                    {s.u}
                  </span>
                </div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
                  {s.l}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        className="relative py-32 sm:py-44 border-t border-[#1A1A1A] overflow-hidden"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <h2 className="font-display uppercase text-[14vw] lg:text-[10vw] leading-[0.85]">
            Build with
            <br />
            <span className="italic text-[#F5F1EA]/55">stone.</span>
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-t border-[#1A1A1A] pt-8">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
              concierge@creditstone.ag · Zürich · Singapore
            </div>
            <button className="font-mono text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 rounded-full bg-[#F5F1EA] text-[#040404]">
              Reserve a seat
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
